import React, { Suspense, lazy } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { vendorRedirect } from 'src/components/shared/v2/presentation';
import { RouteObject } from 'react-router';
import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import portalRoutes from 'src/content/applications/ClientPortal';
import onboardingRoutes from 'src/content/applications/OnboardingPortal';
import vendorRoutes from 'src/content/applications/VendorPortal';
import RecruitmentFeatureGate from 'src/components/RouteGuard/RecruitmentFeatureGate';
import TenantRouteRedirect from 'src/components/TenantRouteRedirect';
import TenantAutoRedirect from 'src/components/TenantAutoRedirect';
import SSOAutoRedirect from 'src/components/SSOAutoRedirect';
import LeaveManagerGuard from 'src/components/RouteGuard/LeaveManagerGuard';

const Loader = (Component) => (props) => (
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Converted Static Imports
const PersonSlide = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/PersonSlide')));
// --- Project Management ---
const Projects = Loader(lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/Projects')));
const AddProjects = Loader(
  lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/Projects/AddProjects'))
);
const ViewProject = Loader(
  lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/Projects/ViewProjects'))
);
const ClientTable = Loader(lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/Client/ClientTable')));
const AddClient = Loader(lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/Client/addClient')));
const ClientDetails = Loader(
  lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/Client/ClientDetails'))
);

// --- Core Auth & Settings ---
const NotificationsPage = Loader(
  lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/layouts/SidebarLayout/Header/Buttons/Notifications/notificationPage'))
);

// --- Employee / User Management ---
const MyDetails = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/MyDetails')));
const Employees = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/MyDetails/Employees')));
const MyDetail = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/MyDetails/MyDetail')));
const EmployeeDirectory = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/employee-directory')));
const EmployeeListV2 = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/EmployeeListV2')));
const OrgChart = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/OrgChart')));
const ProbationDashboard = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Probation')));
const ReportBuilder = Loader(lazy(() => import(/* webpackChunkName: "reports-app" */ 'src/content/applications/Reports/Builder')));
const Reports = Loader(lazy(() => import(/* webpackChunkName: "reports-app" */ 'src/content/applications/Reports')));
const AttendanceSummaryReport = Loader(lazy(() => import(/* webpackChunkName: "reports-app" */ 'src/content/applications/Reports/AttendanceSummary')));
const EmployeeProfilePage = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/EmployeeProfile')));
const UserProfile = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Users/profile')));
const UserSettings = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Users/settings')));
const MyProfile = Loader(lazy(() => import(/* webpackChunkName: "user-profile-app" */ 'src/content/applications/UserProfile/UserProfile')));
const AddEmployeeRds = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/MyDetails/addEmployeeRds')));
const AddEmployee = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/MyDetails/AddEmployee')));
const EmployeeList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/MyDetails/EmployeeListTable')));
const UserDetailsEdit = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/User-Details/ViewUser')));
const UserView = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/User-Details/Users/detailViewUser')));
const UserDetails = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/User-Details/AddUser')));
const UserDetailsList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/User-Details/Users')));
const UserDetailPage = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Users/UserDetailPage')));
// --- Ticket & Support Module ---
const Tickets = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickets')));
const AddTickets = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickets/AddTickets')));
const TicketTool = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickettool/Tickettool')));
const AddTicketTool = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickettool/AddTickettool'))
);
const ViewTicketTool = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickettool/ViewTickettool'))
);
const EditTicketTool = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickettool/EditTickettool'))
);

// --- Helpdesk Module (Phase 1, greenfield — NOT Tickets/ or WorkLog/) ---
const Helpdesk = Loader(lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk')));
const HelpdeskWorkspace = Loader(
  lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk/Workspace'))
);
const HelpdeskMyTickets = Loader(
  lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk/MyTickets'))
);
const HelpdeskNewTicket = Loader(
  lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk/NewTicket'))
);
const HelpdeskAdmin = Loader(
  lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk/Admin'))
);
const HelpdeskTicketDetail = Loader(
  lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk/TicketView'))
);
const HelpdeskIntake = Loader(
  lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk/Intake'))
);
const HelpdeskCsat = Loader(
  lazy(() => import(/* webpackChunkName: "helpdesk-app" */ 'src/content/applications/Helpdesk/Csat'))
);
const TickettoolApp = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickettool')));
// --- Resignation Module ---
const Resignationsubmission = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/ResignationSubmission/index'))
);
const ResignationEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ResignationSubmission/ResignationEdit'))
);
const AddResignation = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ResignationSubmission/AdminAddResignation'))
);
const ResignationApproval = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ResignationSubmission/ResignationApprove'))
);
const ExitInterview = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ResignationSubmission/ExitInterview'))
);

// --- Asset Management Module ---
const AssetDetails = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetDetails/AssetDetails'))
);
const AssetDetailsEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetDetails/AssetDetailsEdit'))
);
const AssetManagementList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetDetails/AssetDetailsList'))
);
const AssetUtilize = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetUtilize/AssetUtilize'))
);
const AssetUtilizeList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetUtilize/AssetUtilizeList'))
);
const AssetUtilizeEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetUtilize/AssetUtilizeEdit'))
);
const AssetService = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetService/AssetService'))
);
const AssetSeviceEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetService/AssetServiceEdit'))
);
const AssetServiceList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetService/AssetServiceList'))
);
const AssetClearance = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetExitForm/AssetClearance'))
);
const AssetExitEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetExitForm/AssetExitEdit'))
);
const AssetExitFormList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetExitForm/AssetExitFormList'))
);
const AssetProfile = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetProfile/AssetProfile'))
);
const InsurancePolicyList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetInsurance/InsurancePolicyList'))
);
const InsurancePolicyForm = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetInsurance/InsurancePolicyForm'))
);
const InsurancePolicyDetail = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetInsurance/InsurancePolicyDetail'))
);
const MyRequestsPage = Loader(
  lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/applications/MyRequests/MyRequestsPage'))
);
const AssetRequestsQueue = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetRequests/AssetRequestsQueue'))
);
const RequestCatalogue = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AssetManagement/AssetRequests/RequestCatalogue'))
);

// --- Workflow V2 Module ---
const WorkflowDashboard = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Dashboard').then((module) => ({
      default: module.Dashboard
    }))
  )
);
const WorkflowBoard = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Board').then((module) => ({
      default: module.TaskBoard
    }))
  )
);
const WorkflowNewTask = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Tasks/NewTask').then((module) => ({
      default: module.NewTask
    }))
  )
);
const WorkflowTasksList = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Tasks').then((module) => ({
      default: module.TasksList
    }))
  )
);
const WorkflowViewTask = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Tasks/ManageTask').then((module) => ({
      default: module.ManageTask
    }))
  )
);
const WorkflowEditTask = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Tasks/EditTask').then((module) => ({
      default: module.EditTask
    }))
  )
);
const WorkflowCreate = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Workflows/CreateWorkflow').then((module) => ({
      default: module.CreateWorkflow
    }))
  )
);
const WorkflowsList = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Workflows/WorkflowsList').then((module) => ({
      default: module.WorkflowsList
    }))
  )
);
const ViewWorkflow = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Workflows/ViewWorkflow').then((module) => ({
      default: module.ViewWorkflow
    }))
  )
);
const WorkflowTemplateCreate = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Templates/CreateTemplate').then((module) => ({
      default: module.CreateTemplate
    }))
  )
);
const WorkflowTemplateEdit = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Templates/EditTemplateWrapper').then(
      (module) => ({ default: module.EditTemplateWrapper })
    )
  )
);
const WorkflowTemplates = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Templates').then((module) => ({
      default: module.Templates
    }))
  )
);
const WorkflowChecklistCreate = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Checklists/CreateChecklist').then((module) => ({
      default: module.CreateChecklist
    }))
  )
);
const WorkflowChecklists = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Checklists').then((module) => ({
      default: module.Checklists
    }))
  )
);
const WorkflowLabels = Loader(lazy(() => import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Labels')));
const WorkflowCalendar = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Calendar').then((module) => ({
      default: module.Calendar
    }))
  )
);
const ComplianceTemplates = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/pages/WorkflowV2/Compliance').then((module) => ({
      default: module.ComplianceTemplatesPage
    }))
  )
);
const ComplianceTemplateDetail = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/pages/WorkflowV2/Compliance').then((module) => ({
      default: module.ComplianceTemplateDetail
    }))
  )
);
const ClientComplianceProfiles = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/pages/WorkflowV2/Compliance').then((module) => ({
      default: module.ClientComplianceProfilesPage
    }))
  )
);
const ProcessBindings = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/pages/WorkflowV2/Compliance').then((module) => ({
      default: module.ProcessBindingsPage
    }))
  )
);
const ComputedDeadlines = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/pages/WorkflowV2/Compliance').then((module) => ({
      default: module.ComputedDeadlinesPage
    }))
  )
);
const ClientComplianceDashboard = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/pages/WorkflowV2/Compliance').then((module) => ({
      default: module.ClientComplianceDashboard
    }))
  )
);
const CustomFieldsExample = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/Examples/CustomFieldsExample'))
);
const PMDashboard = Loader(
  lazy(() =>
    import(/* webpackChunkName: "workflow-v2-app" */ 'src/content/applications/WorkflowV2/PMDashboard').then((module) => ({
      default: module.PMDashboard
    }))
  )
);

// --- SOP Module Routes (NEW) ---
const SOPDocuments = Loader(
  lazy(() => import(/* webpackChunkName: "sop-app" */ 'src/pages/SOP').then((module) => ({ default: module.SOPDocumentsPage })))
);
const SOPViewPage = Loader(
  lazy(() => import(/* webpackChunkName: "sop-app" */ 'src/pages/SOP/SOPViewPage').then((module) => ({ default: module.SOPViewPage })))
);
const SOPCreatePage = Loader(
  lazy(() => import(/* webpackChunkName: "sop-app" */ 'src/pages/SOP/SOPCreatePage').then((module) => ({ default: module.SOPCreatePage })))
);

// --- Workflow Services Module (NEW) ---
const ServiceCatalog = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services'))
);
const ServiceDetail = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/ServiceDetail'))
);
const ServiceForm = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/ServiceForm'))
);
const ClientServicesPage = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/ClientServices'))
);
const ClientServiceForm = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/ClientServiceForm'))
);
const ClientServiceDetail = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/ClientServiceDetail'))
);
const ProjectDetails = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/ProjectDetails'))
);
const ScheduledJobsPage = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/ScheduledJobs'))
);
const ServiceSettingsPage = Loader(
  lazy(() => import(/* webpackChunkName: "workflow-services-app" */ 'src/content/applications/WorkflowV2/Services/Settings'))
);
// --- Status & Pages ---
const Overview = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/overview')));
const LoginLandingPage = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/Login/LandingPage')));
const PostLoginLanding = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/PostLoginLanding')));
const SignIn = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/Login/SignInPage')));
const SignInPageNew = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/Login/SignInPageNew')));
const PostLoginPage = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/Login/PostLoginPage')));
const ForgotPassword = Loader(
  lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/Login/ForgotPasswordPage'))
);
const ResetPassword = Loader(
  lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/Components/Login/ResetPasswordPage'))
);
const Status404 = Loader(lazy(() => import('src/content/pages/Status/Status404')));
const Status500 = Loader(lazy(() => import('src/content/pages/Status/Status500')));
const StatusComingSoon = Loader(lazy(() => import('src/content/pages/Status/ComingSoon')));
const StatusMaintenance = Loader(lazy(() => import('src/content/pages/Status/Maintenance')));

// --- UI Components / Style Guide ---
const Buttons = Loader(lazy(() => import('src/content/pages/Components/Buttons')));
const Modals = Loader(lazy(() => import('src/content/pages/Components/Modals')));
const Accordions = Loader(lazy(() => import('src/content/pages/Components/Accordions')));
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(lazy(() => import('src/content/pages/Components/Badges')));
const Tooltips = Loader(lazy(() => import('src/content/pages/Components/Tooltips')));
const Avatars = Loader(lazy(() => import('src/content/pages/Components/Avatars')));
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));
const CommentsDemoPage = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/pages/CommentsDemoPage')));

// --- Finance & Misc Modules ---
const LoanReqest = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LoanRequest/index')));
const AddLoanReqest = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LoanRequest/AddLoanRequest'))
);
const EditLoanReqest = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LoanRequest/EditLoanRequest'))
);
const ViewLoanReqest = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LoanRequest/ViewLoanRequest'))
);
const SalaryAdvance = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/SalaryAdvance/index')));
const AddSalaryAdvance = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/SalaryAdvance/AddSalaryAdvance'))
);
const EditSalaryAdvance = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/SalaryAdvance/EditSalaryAdvance'))
);
const ViewSalaryAdvance = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/SalaryAdvance/ViewSalaryAdvance'))
);
const Expense = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/expense')));
const AddExpenseDetails = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/expense/addExpense/addExpenseDetails'))
);
const ViewExpense = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/expense/addExpense/viewExpense'))
);
const TeamExpenseView = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/expense/teamExpenseView'))
);
// Approvals-inbox discovery page (WS-F2 follow-up) — deliberately its own
// route, NOT nested under the `expense` route's usePermissions('Expenses')
// gate, since a chain approver may hold no `expenses` Casbin permission at
// all. See MyExpenseApprovals.tsx's doc comment.
const MyExpenseApprovals = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/expense/MyExpenseApprovals'))
);

const MediaManagement = Loader(lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/mediaManagement')));

// --- Support & Maintenance ---
const DefectTracker = Loader(
  lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/DefectTracker/index'))
);
const AddDefectTracker = Loader(
  lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/DefectTracker/DefectTracketAdd'))
);
const EditDefectTracker = Loader(
  lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/DefectTracker/DefectTrackerEdit'))
);
const ViewDefectTracker = Loader(
  lazy(() => import(/* webpackChunkName: "client-project-app" */ 'src/content/applications/DefectTracker/DefectTrackerView'))
);
const TicketTransitionTableList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Ticket Transition/index'))
);
const EditTicketTransition = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Ticket Transition/editTicketTransition'))
);
const TicketSlaList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/TicketSLA')));
const TicketClosure = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Tickettool/TicketClosure'))
);
const MailOptions = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/MailOptions/index')));
const AddMailOptions = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/MailOptions/AddMailOptions'))
);
const MenuSettings = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/MenuSettings/index')));
const ShiftTimingList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftTiming/ShiftTimingList'))
);
const AddShiftTiming = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftTiming/ShiftTimingAdd'))
);
const ShiftTimingEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftTiming/ShiftTimingEdit'))
);

const TemplateManager = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Template/addTemplate')));
const ShiftSwap = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/ShiftSwapRequest')));
const AddShiftSwap = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/ShiftSwapRequest/AddShiftSwap'))
);

// --- Dashboards & Messenger ---
const DynamicDashboard = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/dashboardpage/DynamicDashboard')));
const DashboardByKey = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/dashboardpage/DashboardByKey')));
const CustomizeDashboard = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/dashboardpage/customize/CustomizeDashboard')));
const Dashboard = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/dashboardpage/dashboard')));
const Messenger = Loader(lazy(() => import(/* webpackChunkName: "core-auth" */ 'src/content/applications/Messenger')));

// --- Attendance & Holiday ---
const AttendenceUpload = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/attendenceUpload')));
const AttendenceList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/attendenceLists')));
const DailyAttendance = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/DailyAttendance/DailyAttendance'))
);
const HolidayCalender = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/HolidayCalender')));
const ViewHolidayCalender = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/HolidayCalender/viewHolidays'))
);
const EditHolidayCalender = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/HolidayCalender/editHolidayCalender'))
);
const BuildMyHolidayCalender = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/HolidayCalender/BuildMyCalender'))
);
const AddHolidayCalender = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/HolidayCalender/addHoliday'))
);

// --- Leave Management ---
const LeaveTableList = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Leave')));
const AddLeave = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Leave/AddLeave')));
const AddTeamLeave = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Leave/AddTeamLeave')));
const ViewLeave = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Leave/ViewLeave')));
const EditLeave = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Leave/EditLeaveRequest')));
const LeaveAdjustment = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeaveAdjustment')));
const LeaveAdjustmentTabel = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeaveAdjustment/LeaveAdjustmentTabel'))
);
const LeaveCompOffList = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LeaveCompOff')));
const AddCompOff = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LeaveCompOff/addCompOff')));
const AddTeamCompOffLeave = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LeaveCompOff/addTeamCompOff'))
);
const EditcompLeave = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/LeaveCompOff/editCompOff'))
);
const LeaveTypeList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeaveType')));
const LeaveTypeCreate = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeaveType/AddLeaveType')));
const UpdateLeaveType = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeaveType/UpdateLeaveType'))
);
const ViewLeaveType = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeaveType/viewLeaveType')));

// --- Device Management ---
const DeviceManagement = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/DeviceManagement')));

// --- Attendance v2 — Workday Sheet (Phase 4, read-only) ---
const AttendanceWorkday = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/AttendanceWorkday')));

// --- Overtime Module ---
const OvertimePolicyList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Overtime/OvertimePolicyList'))
);
const OvertimePolicyAdd = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Overtime/OvertimePolicyAdd'))
);
const OvertimePolicyEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Overtime/OvertimePolicyEdit'))
);
const Overtime = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Overtime')));
const OvertimeRecordsList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Overtime/OvertimeRecordsList'))
);
const ManagerOvertimeInbox = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Overtime/ManagerOvertimeInbox'))
);

// --- Shift Rotation Module ---
const ShiftRotationList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftRotation/ShiftRotationList'))
);
const ShiftRotationAdd = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftRotation/ShiftRotationAdd'))
);
const ShiftRotationEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftRotation/ShiftRotationEdit'))
);

// --- Shift Allowance Module ---
const ShiftAllowancePolicyList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAllowance/ShiftAllowancePolicyList'))
);
const ShiftAllowancePolicyAdd = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAllowance/ShiftAllowancePolicyAdd'))
);
const ShiftAllowancePolicyEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAllowance/ShiftAllowancePolicyEdit'))
);
const ShiftAllowance = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAllowance')));
const ShiftAllowanceRecordsList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAllowance/ShiftAllowanceRecordsList'))
);

// --- Shift Assignment Rule Module ---
const ShiftAssignmentRuleList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAssignmentRule/ShiftAssignmentRuleList'))
);
const ShiftAssignmentRuleAdd = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAssignmentRule/ShiftAssignmentRuleAdd'))
);
const ShiftAssignmentRuleEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftAssignmentRule/ShiftAssignmentRuleEdit'))
);

// --- Specializations Module ---
const SpecializationList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Specializations/SpecializationList'))
);
const SpecializationAdd = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Specializations/SpecializationAdd'))
);
const SpecializationEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Specializations/SpecializationEdit'))
);

// --- Practice Areas Module ---
const PracticeAreaList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/PracticeAreas/PracticeAreaList'))
);
const PracticeAreaAdd = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/PracticeAreas/PracticeAreaAdd'))
);
const PracticeAreaEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/PracticeAreas/PracticeAreaEdit'))
);

// --- Positions Module ---
const PositionList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Positions/PositionList')));
const PositionAdd = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Positions/PositionAdd')));
const PositionEdit = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Positions/PositionEdit')));

// --- Shift Planning ---
const ShiftPlan = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftPlan')));
const AddShiftPlan = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftPlan/addShiftPlan')));
const ViewShiftPlan = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftPlan/viewShiftPlan')));
const EditShiftPlan = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftPlan/editShiftPlan')));
const ShiftCoverage = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/shiftCoverage')));
const AddShiftCoverage = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/shiftCoverage/addShiftCoverage'))
);
const ShiftCalender = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftCalender')));
const ShiftPlanExcel = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftCalender/ShiftPlanExcel'))
);
const ShiftUnassigned = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ShiftUnassigned'))
);

// --- Shifts Hub (Phase 1 — consolidated /shifts route) ---
const ShiftsShell = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Shifts/ShiftsShell')));
const AssignPattern = Loader(lazy(() => import('src/content/applications/ShiftPlan/AssignPattern')));
const RosterDayPage = Loader(lazy(() => import('src/content/applications/ShiftPlan/RosterDayPage')));
const PatternDetail = Loader(lazy(() => import('src/content/applications/ShiftPlan/PatternDetail')));
const BulkUploadPage = Loader(lazy(() => import('src/content/applications/ShiftPlan/BulkUploadPage')));
const OnCall = Loader(lazy(() => import('src/content/applications/ShiftPlan/OnCall')));
const OnCallEditor = Loader(lazy(() => import('src/content/applications/ShiftPlan/OnCall').then(module => ({ default: module.OnCallEditor }))));
const MyShiftCalendar = Loader(lazy(() => import('src/content/applications/ShiftCalender/MyShiftCalendar')));
const ShiftsHome = Loader(lazy(() => import('src/content/applications/Shifts/ShiftsViewSwitcher').then(m => ({ default: m.ShiftsHome }))));
const LegacyShiftRedirect = Loader(lazy(() => import('src/content/applications/Shifts/ShiftsViewSwitcher').then(m => ({ default: m.LegacyShiftRedirect }))));

// --- Status Updates & Policy ---
const StatusUpdateList = Loader(lazy(() => import('src/content/applications/StatusUpdate/index')));
const AddStatusUpdate = Loader(
  lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/StatusUpdate/addStatusUpdate'))
);
const ViewStatusUpdate = Loader(lazy(() => import('src/content/applications/StatusUpdate/viewStatusUpdate')));

// --- Timesheet v2 (ts2_*) — parallel new screen; legacy StatusUpdate stays live until cutover ---
const TimesheetPage = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Timesheet')));
const Policy = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Policy')));
const PolicyAdd = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Policy/AddPolicy')));
const PolicyView = Loader(lazy(() => import(/* webpackChunkName: "employee-app" */ 'src/content/applications/Policy/viewPolicy')));
const PolicyUpdateById = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Policy/UpdatePolicy')));

// --- Permissions & Roles ---
const PermissionsList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/permissionList')));
const Permissions = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/permission')));
const Permission = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Permissions')));
const Roles = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/RolesManagement/RolesListPage.tsx')));
const AccessReview = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/AccessReview')));
const AddRolesManagement = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/RolesManagement/AddRolesManagement')));
const EditsRolesManagement = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/RolesManagement/AddRolesManagement'))); // This name is used in routes
const EditRolesManagement = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/RolesManagement/EditRolesManagement')));
const RoleEditV2 = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/RolesManagement/RoleEditV2')));

// Conditional component based on feature flag
const RoleEditComponent = process.env.REACT_APP_FEATURE_NEW_RBAC === 'true' ? RoleEditV2 : EditsRolesManagement;

// V2 is the default; set REACT_APP_FEATURE_EMPLOYEE_LIST_V1=true to revert to the old directory
const EmployeeListComponent = process.env.REACT_APP_FEATURE_EMPLOYEE_LIST_V1 === 'true' ? EmployeeDirectory : EmployeeListV2;

// --- System Admin ---
const SchedulerDashboard = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/SystemAdmin/SchedulerDashboard'))
);

// --- Settings & Configuration ---
const OrganisationProfile = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/datepage')));
const OrganisationProfileUpdate = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/organisationProfileUpdate'))
);
const AddWorkLocations = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/AddworkLocations'))
);
const WorkLocationEdit = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/WorkLocationEdit'))
);
const WorkLocationList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/worklocationData'))
);
const EmployeeNumberingSettings = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/EmployeeNumbering'))
);
const MenuOrderSettings = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/MenuOrder')));
const AddDepartments = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/AddDepartments')));
const DepartmentsList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/departmentData')));
const EditDepartments = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/departmentEdit')));
const AddDesignation = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/AddDesignation')));
const EditDesignation = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/designationEdit')));
const DesignationList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Settings/designationData')));

// Forms Module
const FormsList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Forms/FormsList')));
const FormEditor = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Forms/FormEditor')));
const CodeReference = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/CodeReference/AddCodeReference')));
const CodeReferenceList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/CodeReference/CodeReferenceListPage')));
const CodeReferenceEdit = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/CodeReference/UpdatecodeReference')));
const FeatureRoleAccess = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/FeatureRoleAccess')));
const AddFeatureRoleAccess = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/FeatureRoleAccess/addFeatureRoleAccess')));
const EditFeatureRoleAccess = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/FeatureRoleAccess/editFeatureRoleAccess')));
const OfferLetterGenerator = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/User-Details/Users/offerLetterGenerator')));
const OfferLettersList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/OnboardingPortal/Admin/OfferLettersList')));
const InviteNewHire = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/OnboardingPortal/Admin/InviteNewHire')));
const OnboardingSessions = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/OnboardingPortal/Admin/OnboardingSessions')));

// --- Recruitment ---
const JobPostingsList = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/JobPostings/JobPostingsList')));
const ApplicationDetailPage = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Applications/ApplicationDetailPage')));
const AddCandidate = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Candidates/AddCandidate')));
const PipelinePage = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Pipeline/PipelinePage')));
const RecruitmentSettingsPage = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Settings/RecruitmentSettingsPage')));
const VendorsList = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Vendors/VendorsList')));
const VendorDetail = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Vendors/VendorDetail')));
const InterviewsPage = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Interviews/InterviewsPage')));
const MyInterviewsPage = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Interviews/MyInterviewsPage')));
const CandidatesList = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Candidates/CandidatesList')));
const CreateJobPage = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/JobPostings/CreateJobPage')));
const InterviewersPage = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/Interviewers/InterviewersPage')));
const ResourceRequestList = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/ResourceRequests/ResourceRequestList')));
const ResourceRequestCreate = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/ResourceRequests/ResourceRequestCreate')));
const ResourceRequestDetail = Loader(lazy(() => import(/* webpackChunkName: "recruitment-app" */ 'src/content/applications/Recruitment/ResourceRequests/ResourceRequestDetail')));
const AddTemplate = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Template/addTemplate')));

// --- Leave Hub (Phase 0.2 — real shell wired in) ---
const LeaveShell = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Leave/LeaveShell')));
// Phase 1B.5 — Team Leaves screen
const TeamLeave = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/Leave/TeamLeave')));
// Phase 1B.7 / Wave 4 — Leave Calendar (v2, day-row-aware).
// Legacy component at Leave/LeaveCalendar is kept but no longer wired to /leave/calendar.
const LeaveCalendar = Loader(lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/LeaveCalendar')));
// Wave 1 Sprint B3 — Employee Group Manager
const EmployeeGroupManager = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeavePolicy/EmployeeGroupManager'))
);
// Wave 1 Sprint B1 — Leave Policy Admin (List + Detail)
const LeavePolicyList = Loader(lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeavePolicy/PolicyList')));
const LeavePolicyDetail = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeavePolicy/PolicyDetail'))
);
// Wave 2 Sprint P2-A1 — Attribute Predicate Editor
const AttributePredicatesManager = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/LeavePolicy/AttributePredicatesManager'))
);

// WS-H7 — Expense Policy Admin (List + Detail + Category Attribute Admin)
// Routes mirror the WS-B0-seeded sub_category rows (route_path):
//   expense_policy        -> /expense/admin/policies
//   create_expense_policy -> /expense/admin/policies/create
//   edit_expense_policy   -> /expense/admin/policies/:id
// Gated behind EXPENSE_ADMIN_POLICY_EDITOR + usePermissions('expense_policy') inside each screen.
const ExpensePolicyList = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ExpensePolicy/PolicyList'))
);
const ExpensePolicyDetail = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ExpensePolicy/PolicyDetail'))
);
const ExpenseCategoryAttributeAdmin = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ExpensePolicy/CategoryAttributeAdmin'))
);
const ExpenseFxRatesAdmin = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ExpensePolicy/FxRatesAdmin'))
);
// ExpenseTravelAdvanceAdmin route DISABLED as containment for the
// travel-advance double-charge issue #238 (release-gate 2026-09-03, Blocker B1)
// — see backend-go/internal/modules/employee_benefits/routes.go for the
// backend side of this containment. The lazy import + component are
// removed here rather than left dead, since the screen is unreachable
// without the router entry below and the backend routes it calls are gone.
// Expense Policy Iteration — expense_approver_map admin CRUD. Same
// no-dedicated-sidebar-entry / self-gated pattern as the routes above.
const ExpenseApproverMapAdmin = Loader(
  lazy(() => import(/* webpackChunkName: "admin-app" */ 'src/content/applications/ExpensePolicy/ApproverMapAdmin'))
);

// --- Leave v2 (Stage 3 — engine-driven) ---
// Agent A: MyLeaveHome. Agent B: ApplyLeave + MyLeaveDetail (stubs here, bodies replaced by B).
// Stage 3 FE slice 3: ApprovalsInbox (approver inbox + override UI).
const MyLeaveHome = Loader(lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/MyLeaveHome')));
const ApplyLeave = Loader(lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/ApplyLeave')));
const MyLeaveDetail = Loader(lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/MyLeaveDetail')));
const ApprovalsInbox = Loader(lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/ApprovalsInbox')));
// Full-page review screen for a single request (/leave/approvals/:id)
const LeaveReviewPage = Loader(
  lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/LeaveReviewPage'))
);
// Stage 3 FE — Leave Type Admin
const LeaveTypeAdmin = Loader(lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/LeaveTypeAdmin')));
// Stage 3 FE — Leave Balance Admin (adjust, opening balance, team view)
const LeaveBalanceAdmin = Loader(
  lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/LeaveBalanceAdmin'))
);
// Phase 0 — Policy Template Admin (list + instantiate; authoring UI deferred)
const LeavePolicyTemplates = Loader(
  lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/LeavePolicyTemplates'))
);
// Comp-off earn: employee's own requests list (/leave/compoff/earn)
const CompOffEarnApply = Loader(
  lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/CompOffEarnApply'))
);
// Comp-off earn: apply form on its own page (/leave/compoff/earn/new)
const CompOffEarnNew = Loader(
  lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/CompOffEarnNew'))
);
// Comp-off earn: admin/HR inbox (/leave/compoff/inbox)
const CompOffEarnInbox = Loader(
  lazy(() => import(/* webpackChunkName: "leave-v2-app" */ 'src/content/applications/LeaveV2/CompOffEarnInbox'))
);

const LeavePagePlaceholder: React.FC<{ name: string }> = ({ name }) => (
  <div style={{ padding: 24 }}>Placeholder: {name} — Phase 1B will fill this in.</div>
);

// --- Onboarding V2 ---
const OnboardingWizard = Loader(
  lazy(() => import(/* webpackChunkName: "onboarding-v2-app" */ 'src/content/applications/OnboardingV2'))
);
const InviteCodesAdmin = Loader(
  lazy(() => import(/* webpackChunkName: "onboarding-v2-app" */ 'src/content/applications/OnboardingV2/InviteCodesAdmin'))
);
const SubscriptionPlansV2 = Loader(
  lazy(() => import(/* webpackChunkName: "onboarding-v2-app" */ 'src/content/applications/SubscriptionPlansV2'))
);
const PayablesVendors = Loader(lazy(() => import('src/content/applications/Payables/Vendors')));
const AgreementPage = Loader(lazy(() => import('src/content/applications/Payables/AgreementPage')));
function LegacyVendorRedirect() {
  const location = useLocation();
  return <Navigate replace to={vendorRedirect(location.pathname, location.search) + location.hash} />;
}
const PayablesBills = Loader(lazy(() => import('src/content/applications/Payables/Bills')));
const PayablesPayments = Loader(lazy(() => import('src/content/applications/Payables/Payments')));
const PayablesSettings = Loader(lazy(() => import('src/content/applications/Payables/Settings')));
const OrgSettingsV2 = Loader(
  lazy(() => import(/* webpackChunkName: "org-settings-v2-app" */ 'src/content/applications/OrgSettingsV2'))
);

// Dark-launch: every portal route except the (inert without API) login page sits behind the
// tenant recruitment flag. Login stays ungated because the flag endpoint needs authentication.
const gatePortalChildren = (children: RouteObject[]): RouteObject[] => {
  const login = children.filter((c) => c.path === 'login');
  const rest = children.filter((c) => c.path !== 'login');
  return [
    ...login,
    {
      element: (
        <RecruitmentFeatureGate>
          <Outlet />
        </RecruitmentFeatureGate>
      ),
      children: rest
    }
  ];
};

const routes: RouteObject[] = [
  {
    path: 'vendors', element: <SidebarLayout />, children: [
      { path: '', element: <PayablesVendors /> },
      { path: ':id', element: <PayablesVendors /> },
      { path: ':id/:tab', element: <PayablesVendors /> }
    ]
  },
  {
    path: 'agreements', element: <SidebarLayout />, children: [
      { path: 'new', element: <AgreementPage key="new" /> },
      { path: ':id', element: <AgreementPage key="detail" /> },
      { path: ':id/edit', element: <AgreementPage key="edit" /> },
      { path: ':id/amend', element: <AgreementPage key="amend" /> }
    ]
  },
  {
    path: 'payables',
    element: <SidebarLayout />,
    children: [
      { path: 'vendors', element: <LegacyVendorRedirect /> },
      { path: 'vendors/:id', element: <LegacyVendorRedirect /> },
      { path: 'bills', element: <PayablesBills /> },
      { path: 'bills/new', element: <PayablesBills key="bill-new" /> },
      { path: 'bills/:id', element: <PayablesBills key="bill-detail" /> },
      { path: 'bills/:id/edit', element: <PayablesBills key="bill-edit" /> },
      { path: 'payments', element: <PayablesPayments /> },
      { path: 'payments/:id', element: <PayablesPayments key="payment-detail" /> },
      { path: 'payments/batches/:id', element: <PayablesPayments key="batch-detail" /> },
      { path: 'casual-payees', element: <PayablesSettings section="casual-payees" /> },
      { path: 'tax', element: <PayablesSettings section="tax" /> },
      { path: 'tax/sections/new', element: <PayablesSettings section="tax" /> },
      { path: 'tax/sections/:id', element: <PayablesSettings section="tax" /> },
      { path: 'tax/sections/:id/edit', element: <PayablesSettings section="tax" /> },
      { path: 'tax/certificates/new', element: <PayablesSettings section="tax" /> },
      { path: 'tax/certificates/:id', element: <PayablesSettings section="tax" /> },
      { path: 'tax/certificates/:id/edit', element: <PayablesSettings section="tax" /> },
      { path: 'exceptions', element: <PayablesSettings section="exceptions" /> }
    ]
  },
  {
    path: 'home',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <PostLoginLanding />
      }
    ]
  },
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: (
          <TenantAutoRedirect>
            <SSOAutoRedirect>
              <LoginLandingPage />
            </SSOAutoRedirect>
          </TenantAutoRedirect>
        )
      },
      {
        path: '/login',
        element: (
          <TenantAutoRedirect>
            <SSOAutoRedirect>
              <SignInPageNew />
            </SSOAutoRedirect>
          </TenantAutoRedirect>
        )
      },
      {
        path: '/login-old',
        element: (
          <TenantAutoRedirect>
            <SSOAutoRedirect>
              <SignIn />
            </SSOAutoRedirect>
          </TenantAutoRedirect>
        )
      },
      {
        path: '/post-login',
        element: <PostLoginPage />
      },
      {
        path: '/forget-password',
        element: (
          <TenantAutoRedirect>
            <ForgotPassword />
          </TenantAutoRedirect>
        )
      },
      {
        path: '/resetpassword/:id',
        element: <ResetPassword />
      },
      {
        path: '/t/:tenant/resetpassword/:id',
        element: <ResetPassword />
      },
      // /support/csat/:token — CSAT (customer satisfaction survey) submission
      // page (Phase 3). Truly public/anonymous: registered here (under
      // BaseLayout, no auth guard) rather than in the authenticated
      // SidebarLayout Helpdesk tree below (compare with /support/:formKey's
      // NOTE a few blocks down, which explains why THAT route is still
      // auth-gated) because the backend route it calls
      // (POST /v1/helpdesk/csat/:token) is registered outside the
      // JWT-guarded group — see helpdesk.go's RegisterCSATRoutes wiring.
      {
        path: '/support/csat/:token',
        element: <HelpdeskCsat />
      },
      {
        path: 't/:tenantRef/*',
        element: <TenantRouteRedirect />
      },
      {
        path: 'overview',
        element: <Navigate to="/" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '/nlaunch/signup',
        element: <OnboardingWizard />
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboard',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        // element: <Navigate to="1" replace />
        element: <PostLoginLanding />
      },
      // Company customisation of the built-in dashboards (needs dashboardmanage.update; the
      // screen shows a no-access message otherwise).
      {
        path: 'customize',
        element: <CustomizeDashboard />
      },
      {
        path: 'customize/:key',
        element: <CustomizeDashboard />
      },
      {
        path: ':id',
        element: <DynamicDashboard />
      },
      // The Team and Admin dashboards have their own named addresses (Admin is the `hr` dashboard).
      {
        path: 'team',
        element: <DashboardByKey dashboardKey="team" />
      },
      {
        path: 'admin',
        element: <DashboardByKey dashboardKey="hr" />
      },
      {
        path: 'by-key/:key',
        element: <DashboardByKey />
      },
      {
        path: 'messenger',
        element: <Messenger />
      },
      {
        path: 'comments-demo/:entityId?',
        element: <CommentsDemoPage />
      }
    ]
  },
  {
    path: 'workflowv2',
    element: <SidebarLayout />,
    children: [
      {
        path: 'dashboard',
        element: <WorkflowDashboard />
      },
      // {
      //   path: 'pm-dashboard',
      //   element: <PMDashboard />
      // },
      {
        path: 'board',
        element: <WorkflowBoard />
      },
      {
        path: 'tasks',
        element: <WorkflowTasksList />
      },
      {
        path: 'tasks/add',
        element: <WorkflowNewTask />
      },
      {
        path: 'tasks/:id',
        element: <WorkflowViewTask />
      },
      {
        path: 'tasks/:id/edit',
        element: <WorkflowEditTask />
      },
      {
        path: 'tasks/create-workflow',
        element: <WorkflowCreate />
      },
      {
        path: 'workflows',
        element: <WorkflowsList />
      },
      {
        path: 'workflows/:id',
        element: <ViewWorkflow />
      },
      {
        path: 'subtasks',
        element: <Navigate to="/workflowv2/tasks" replace />
      },
      {
        path: 'subtasks/add',
        element: <Navigate to="/workflowv2/tasks/add" replace />
      },
      {
        path: 'subtasks/:id',
        element: <Navigate to="/workflowv2/tasks" replace />
      },
      {
        path: 'subtasks/:id/edit',
        element: <Navigate to="/workflowv2/tasks" replace />
      },
      {
        path: 'checklists',
        element: <WorkflowChecklists />
      },
      {
        path: 'checklists/create',
        element: <WorkflowChecklistCreate />
      },
      {
        path: 'templates',
        element: <WorkflowTemplates />
      },
      {
        path: 'templates/create',
        element: <WorkflowTemplateEdit />
      },
      {
        path: 'templates/:id/edit',
        element: <WorkflowTemplateEdit />
      },
      {
        path: 'labels',
        element: <WorkflowLabels />
      },
      {
        path: 'calendar',
        element: <WorkflowCalendar />
      },
      {
        path: 'compliance/templates',
        element: <ComplianceTemplates />
      },
      {
        path: 'compliance/templates/:id',
        element: <ComplianceTemplateDetail />
      },
      {
        path: 'compliance/client-profiles',
        element: <ClientComplianceProfiles />
      },
      {
        path: 'compliance/process-bindings',
        element: <ProcessBindings />
      },
      {
        path: 'compliance/deadlines',
        element: <ComputedDeadlines />
      },
      {
        path: 'compliance/dashboard',
        element: <ClientComplianceDashboard />
      },
      {
        path: 'examples/custom-fields',
        element: <CustomFieldsExample />
      },
      // --- SOP Module Routes (NEW) ---
      {
        path: 'sop',
        element: <SOPDocuments />
      },
      {
        path: 'sop/create',
        element: <SOPCreatePage />
      },
      {
        path: 'sop/:id',
        element: <SOPViewPage />
      },
      {
        path: 'sop/:id/edit',
        element: <SOPCreatePage />
      },
      // --- Services Module Routes (NEW) ---
      {
        path: 'services',
        element: <ServiceCatalog />
      },
      {
        path: 'services/create',
        element: <ServiceForm />
      },
      {
        path: 'services/:id/edit',
        element: <ServiceForm />
      },
      {
        path: 'services/client-services',
        element: <ClientServicesPage />
      },
      {
        path: 'services/client-services/create',
        element: <ClientServiceForm />
      },
      {
        path: 'services/client-services/:id/edit',
        element: <ClientServiceForm />
      },
      {
        path: 'services/client-services/:id',
        element: <ClientServiceDetail />
      },
      {
        path: 'services/projects/:id',
        element: <ProjectDetails />
      },
      {
        path: 'services/scheduled-jobs',
        element: <ScheduledJobsPage />
      },
      {
        path: 'services/settings',
        element: <ServiceSettingsPage />
      },
      {
        path: 'services/:id',
        element: <ServiceDetail />
      }
    ]
  },

  {
    // My Requests / My Approvals (#328): ungated self-service page; every row is authorized by its module.
    path: 'my-requests',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <MyRequestsPage />
      }
    ]
  },

  {
    path: 'notifications',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <NotificationsPage />
      }
    ]
  },

  {
    path: 'status-update',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <StatusUpdateList />
      }
    ]
  },
  {
    path: 'add-status-update',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <AddStatusUpdate />
      }
    ]
  },
  {
    path: 'edit-status-update/:id',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <AddStatusUpdate />
      }
    ]
  },
  {
    path: 'view-status-update/:id',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ViewStatusUpdate />
      }
    ]
  },

  {
    path: 'timesheet',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <TimesheetPage />
      },
      {
        path: 'team',
        element: <TimesheetPage />
      },
      {
        path: 'approvals',
        element: <TimesheetPage />
      },
      {
        path: 'utilisation',
        element: <TimesheetPage />
      },
      {
        path: 'rates',
        element: <TimesheetPage />
      }
    ]
  },

  {
    path: 'resignation-submission',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Resignationsubmission />
      }
    ]
  },
  {
    path: 'resignation-approval/:id',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ResignationApproval />
      }
    ]
  },
  {
    path: 'exit-interview/:id',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ExitInterview />
      }
    ]
  },

  {
    path: 'add-employee',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <AddEmployeeRds />
      }
    ]
  },

  // {
  //   path: 'employee-list',
  //   element: <SidebarLayout />,
  //   children: [
  //     {
  //       path: '',
  //       element: <  EmployeeList />
  //     }
  //   ]
  // },

  {
    path: 'add-resignation',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <AddResignation />
      }
    ]
  },

  {
    path: 'resignation-edit/:id',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ResignationEdit />
      }
    ]
  },
  {
    path: 'employee',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'add',
        element: <AddEmployee />
      },
      {
        path: 'employee-details',
        element: <MyDetails />
      },
      {
        path: 'view/:id',
        element: <Employees />
      },
      {
        path: 'profile/:id',
        element: <EmployeeProfilePage />
      }
    ]
  },
  {
    path: 'my-detail',
    element: <SidebarLayout />,
    children: [
      {
        path: 'view/:id',
        element: <MyDetail />
      }
    ]
  },
  {
    path: 'clients',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <ClientTable />
      },
      {
        path: 'AddClient',
        element: <AddClient />
      },
      {
        path: 'EditClient/:id',
        element: <AddClient />
      },
      {
        path: 'ViewClient/:id',
        element: <ClientDetails />
      },
      {
        path: ':id',
        element: <ClientDetails />
      }
    ]
  },
  {
    path: 'management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'attendence-upload',
        element: <AttendenceUpload />
      },
      {
        path: 'attendence-lists',
        element: <AttendenceList />
      },
      {
        path: 'permissions',
        element: <PermissionsList />
      },

      {
        path: 'permissions-lists/:id',
        element: <Permissions />
      },

      {
        path: 'expense',
        element: <Expense />
      },

      {
        path: 'expense-approvals',
        element: <MyExpenseApprovals />
      },

      {
        path: 'expense/:userExpenseId/:itemId/view',
        element: <ViewExpense />
      },
      {
        path: 'teamexpense/:userExpenseId/:itemId/view',
        element: <TeamExpenseView />
      },
      {
        path: 'add-expense-details/:actions',
        element: <AddExpenseDetails />
      },

      {
        path: 'expense/:userExpenseId/:itemId/:actions',
        element: <AddExpenseDetails />
      },

      {
        path: 'ticket-tool/netops',
        element: <TickettoolApp />
      },

      {
        path: 'add-ticket-tool/netops',
        element: <AddTicketTool />
      },
      {
        path: 'add-ticket-tool/netops/:id',
        element: <AddTicketTool />
      },
      {
        path: 'view-ticket-tool/netops/:id',
        element: <ViewTicketTool />
      },
      {
        path: 'edit-ticket-tool/netops/:id',
        element: <EditTicketTool />
      },
      {
        path: 'ticket-tool/ticket-closure/er',
        element: <TicketClosure />
      },
      {
        path: 'ticket-tool/ticket-closure/netops',
        element: <TicketClosure />
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  },

  // User Profile - accessible to all signed-in users
  {
    path: 'my-profile',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <MyProfile />
      }
    ]
  },

  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'code-reference',
        element: <CodeReference />
      },

      {
        path: 'code-reference-list',
        element: <CodeReferenceList />
      },

      {
        path: 'code-reference-edit/:id',
        element: <CodeReferenceEdit />
      },

      {
        path: 'feature-role-access-list',
        element: <FeatureRoleAccess />
      },

      {
        path: 'feature-role-access',
        element: <AddFeatureRoleAccess />
      },

      {
        path: 'feature-role-access-edit/:id',
        element: <EditFeatureRoleAccess />
      },

      {
        path: 'offer-letter-generator',
        element: <OfferLetterGenerator />
      },
      // Recruitment / onboarding / vendor admin routes: dark-launched behind the tenant
      // recruitment flag (fails closed -> 404). Pathless layout route, so URLs are unchanged.
      {
        element: (
          <RecruitmentFeatureGate>
            <Outlet />
          </RecruitmentFeatureGate>
        ),
        children: [
        {
          path: 'offer-letters-onboarding',
          element: <OfferLettersList />
        },
        {
          path: 'onboarding-admin/invite-new-hire',
          element: <InviteNewHire />
        },
        {
          path: 'onboarding-admin/sessions',
          element: <OnboardingSessions />
        },
        {
          path: 'recruitment/jobs',
          element: <JobPostingsList />
        },
        {
          path: 'recruitment/jobs/new',
          element: <CreateJobPage />
        },
        {
          path: 'recruitment/jobs/:id/edit',
          element: <CreateJobPage />
        },
        {
          path: 'recruitment/candidates',
          element: <CandidatesList />
        },
        {
          path: 'recruitment/applications/:id',
          element: <ApplicationDetailPage />
        },
        {
          path: 'recruitment/candidates/new',
          element: <AddCandidate />
        },
        {
          path: 'recruitment/pipeline',
          element: <PipelinePage />
        },
        {
          path: 'recruitment/applications',
          element: <PipelinePage />
        },
        {
          path: 'recruitment/interviews',
          element: <InterviewsPage />
        },
        {
          path: 'recruitment/interviews/mine',
          element: <MyInterviewsPage />
        },
        {
          path: 'recruitment/settings',
          element: <RecruitmentSettingsPage />
        },
        {
          path: 'recruitment/vendors',
          element: <VendorsList />
        },
        {
          path: 'recruitment/vendors/:id',
          element: <VendorDetail />
        },
        {
          path: 'recruitment/interviewers',
          element: <InterviewersPage />
        },
        {
          path: 'recruitment/resource-requests',
          element: <ResourceRequestList />
        },
        {
          path: 'recruitment/resource-requests/new',
          element: <ResourceRequestCreate />
        },
        {
          path: 'recruitment/resource-requests/:id',
          element: <ResourceRequestDetail />
        },
        {
          path: 'recruitment/offer-templates',
          element: <AddTemplate />
        },
        ]
      },
      {
        path: 'system-scheduler',
        element: <SchedulerDashboard />
      }
    ]
  },

  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'roles',
        element: <Roles />
      },
      {
        path: 'add-roles',
        element: <AddRolesManagement />
      },
      {
        path: 'edit-roles/:id',
        element: <RoleEditComponent />
      },
      {
        // Admin-only diagnostic screen for GET /access-review/explain and
        // GET /access-review/view-as (authz observability, Phase 1). The
        // backend enforces who may target another user; this route is not
        // gated on the frontend beyond normal authentication.
        path: 'access-review',
        element: <AccessReview />
      }
    ]
  },

  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'offer-letter-template',
        element: <AddTemplate />
      }
    ]
  },

  {
    path: 'media-management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <MediaManagement />
      }
    ]
  },
  // employee-list
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'employee-list',
        element: <EmployeeListComponent />
      },
      {
        path: 'org-chart',
        element: <OrgChart />
      },
      {
        path: 'probation',
        element: <ProbationDashboard />
      },
      {
        path: 'reports',
        children: [
          {
            path: '',
            element: <Reports />
          },
          {
            path: 'create',
            element: <ReportBuilder />
          },
          {
            path: 'edit/:id',
            element: <ReportBuilder />
          },
          {
            path: 'attendance-summary',
            element: <AttendanceSummaryReport />
          }
        ]
      }
    ]
  },

  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'user-details',
        element: <UserDetails />
      },

      {
        path: 'user-list',
        element: <UserDetailsList />
      },

      {
        path: 'user-edit/:id',
        element: <UserDetailsEdit />
      },
      {
        path: 'view-user/:id',
        element: <UserView />
      },
      {
        path: 'users/new',
        element: <UserDetailPage />
      },
      {
        path: 'users/:id',
        element: <UserDetailPage />
      }
    ]
  },

  //Asset management module
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'assetdetails/add',
        element: <AssetDetails />
      },
      {
        path: 'asset-details/list',
        element: <AssetManagementList />
      },
      {
        path: 'assetdetails/:id',
        element: <AssetDetailsEdit />
      },
      {
        path: 'assetutilize/add',
        element: <AssetUtilize />
      },
      {
        path: 'asset-utilization/list',
        element: <AssetUtilizeList />
      },
      {
        path: 'assetutilize/:id',
        element: <AssetUtilizeEdit />
      },

      {
        path: 'asset-service/add',
        element: <AssetService />
      },
      {
        path: 'asset-service/:id',
        element: <AssetSeviceEdit />
      },
      {
        path: 'asset-service/:id',
        element: <AssetSeviceEdit />
      },
      {
        path: 'asset-service/list',
        element: <AssetServiceList />
      },

      {
        // The single-form add screen is replaced by the multi-asset clearance (plan §6.4);
        // AssetExitEdit keeps showing old records.
        path: 'asset-exitform/add',
        element: <Navigate to="/asset-exitform/clearance" replace />
      },
      {
        path: 'asset-exitform/clearance',
        element: <AssetClearance />
      },

      {
        path: 'asset-exitform/:id',
        element: <AssetExitEdit />
      },
      {
        path: 'asset-exitform/list',
        element: <AssetExitFormList />
      },
      {
        path: 'asset-profile/:assetId',
        element: <AssetProfile />
      },
      // Asset insurance (plan §5.6)
      {
        path: 'asset-insurance/list',
        element: <InsurancePolicyList />
      },
      {
        path: 'asset-insurance/add',
        element: <InsurancePolicyForm />
      },
      {
        path: 'asset-insurance/:id/edit',
        element: <InsurancePolicyForm />
      },
      {
        path: 'asset-insurance/:id',
        element: <InsurancePolicyDetail />
      },
      // Asset requests (plan §8.9): the deciders' queue tabs plus, for every employee, the "My assets & requests" tab (held assets, raise, follow).
      {
        path: 'asset-requests/catalogue',
        element: <RequestCatalogue />
      },
      {
        path: 'asset-requests',
        element: <AssetRequestsQueue />
      }
    ]
  },

  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: '/ticket-transition/list',
        element: <TicketTransitionTableList />
      },

      {
        path: '/ticket-transition/edit/:id',
        element: <EditTicketTransition />
      },

      {
        // Redirect: old bookmark → new consolidated /shifts hub
        path: '/shift-coverage/list',
        element: <Navigate to="/shifts/coverage" replace />
      },
      {
        path: '/shift-coverage/add',
        element: <AddShiftCoverage />
      },
      {
        // Redirect: old bookmark → new consolidated /shifts hub
        path: '/shift-plan/list',
        element: <Navigate to="/shifts/patterns" replace />
      },

      {
        path: '/add-shift-plan',
        element: <AddShiftPlan />
      },

      {
        path: '/shift-plan/view/:id',
        element: <ViewShiftPlan />
      },

      {
        path: '/shift-plan/edit/:id',
        element: <EditShiftPlan />
      },

      {
        // Redirect: old bookmark → new consolidated /shifts hub
        path: '/shift-calender/list',
        element: <Navigate to="/shifts/roster?view=calendar" replace />
      },
      {
        path: '/daily-attendance/list',
        element: <DailyAttendance />
      },

      {
        path: '/holiday-calender/list',
        element: <HolidayCalender />
      },

      {
        path: '/view-holiday-calender/:id',
        element: <ViewHolidayCalender />
      },
      {
        path: '/edit-holiday-calender/:id',
        element: <EditHolidayCalender />
      },

      {
        path: '/build-holiday-calender/:id',
        element: <BuildMyHolidayCalender />
      },

      {
        path: '/add-holiday-calender',
        element: <AddHolidayCalender />
      },

      {
        path: 'policy/list',
        element: <Policy />
      },

      {
        path: '/policy/add',
        element: <PolicyAdd />
      },

      {
        path: 'view-policy/:id',
        element: <PolicyView />
      },
      {
        path: 'edit-policy/:id',
        element: <PolicyUpdateById />
      },

      {
        path: 'leave-list',
        element: <LeaveTableList />
      },
      {
        path: 'add-leave-request',
        element: <AddLeave />
      },

      {
        path: 'view-leave/:id',
        element: <ViewLeave />
      },
      {
        path: 'edit-leave-request/:id',
        element: <EditLeave />
      },
      {
        path: 'device-management',
        element: <DeviceManagement />
      },
      {
        path: 'attendance-workday',
        element: <AttendanceWorkday />
      },
      {
        path: 'leave-type-list',
        element: <LeaveTypeList />
      },
      {
        path: 'leave-type-create',
        element: <LeaveTypeCreate />
      },
      {
        path: 'upDate-leave-type/:id',
        element: <UpdateLeaveType />
      },
      {
        path: 'view-leave-type/:id',
        element: <ViewLeaveType />
      },
      {
        path: 'leave-adjustment',
        element: <LeaveAdjustment />
      },

      {
        path: 'permission',
        element: <Permission />
      },

      {
        path: 'leave-comp-off',
        element: <LeaveCompOffList />
      },
      {
        path: 'add-leaveCompOff',
        element: <AddCompOff />
      },
      {
        path: 'view-leaveCompOff',
        element: <ViewLeave />
      },
      {
        path: 'edit-leaveCompOff/:id',
        element: <EditcompLeave />
      },
      {
        path: 'organisationprofile',
        element: <OrgSettingsV2 />
      },
      {
        path: 'organisationprofile/edit/:id',
        element: <OrganisationProfileUpdate />
      },
      {
        path: 'work-locations/add',
        element: <AddWorkLocations />
      },
      {
        path: 'work-locations/edit/:id',
        element: <WorkLocationEdit />
      },
      {
        path: 'work-locations/list',
        element: <WorkLocationList />
      },
      {
        path: 'employee-numbering/rules',
        element: <EmployeeNumberingSettings />
      },
      {
        path: 'menu-order',
        element: <MenuOrderSettings />
      },
      {
        path: 'department/add',
        element: <AddDepartments />
      },
      {
        path: 'department/list',
        element: <DepartmentsList />
      },
      {
        path: 'department/edit/:id',
        element: <EditDepartments />
      },
      {
        path: 'designation/add',
        element: <AddDesignation />
      },
      {
        path: 'designation/edit/:id',
        element: <EditDesignation />
      },
      {
        path: 'designation/list',
        element: <DesignationList />
      },
      {
        path: 'forms',
        element: <FormsList />
      },
      {
        path: 'forms/new',
        element: <FormEditor />
      },
      {
        path: 'forms/:id',
        element: <FormEditor />
      },
      {
        path: 'ticket-sla/list',
        element: <TicketSlaList />
      },
      {
        path: 'mail-options-list',
        element: <MailOptions />
      },
      {
        path: 'mail-options-add',
        element: <AddMailOptions />
      },
      {
        path: `mail-options-edit/:id`,
        element: <AddMailOptions />
      }
    ]
  },

  {
    path: 'add-team-leave-request',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <AddTeamLeave />
      }
    ]
  },
  {
    path: 'add-team-comp-off-leave-request',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <AddTeamCompOffLeave />
      }
    ]
  },

  //Taskmodule
  {
    path: 'management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="user" replace />
      },

      {
        path: '',
        element: <Navigate to="projects" replace />
      },
      {
        path: 'projects',
        element: <Projects />
      },
      {
        path: 'projects/:id',
        element: <AddProjects />
      },
      {
        path: 'ViewProjects/:id',
        element: <ProjectDetails />
      },
      {
        path: '',
        element: <Navigate to="tickets" replace />
      },
      {
        path: 'ticket-tool/er',
        element: <TickettoolApp />
      },

      {
        path: 'edit-ticket-tool/er/:id',
        element: <EditTicketTool />
      },

      {
        path: 'add-ticket-tool/er',
        element: <AddTicketTool />
      },
      {
        path: 'add-ticket-tool/er/:id',
        element: <AddTicketTool />
      },
      {
        path: 'view-ticket-tool/er/:id',
        element: <ViewTicketTool />
      },
      {
        path: 'addprojects',
        element: <AddProjects />
      }
    ]
  },
  {
    path: 'defect-tracker',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'list',
        element: <DefectTracker />
      },
      {
        path: 'Add',
        element: <AddDefectTracker />
      },
      {
        path: 'Edit/:id',
        element: <EditDefectTracker />
      },
      {
        path: 'View/:id',
        element: <ViewDefectTracker />
      }
    ]
  },

  {
    path: 'finance',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="list" replace />
      },
      {
        path: 'loan-request/list',
        element: <LoanReqest />
      },
      {
        path: 'loan-request/Add',
        element: <AddLoanReqest />
      },
      {
        path: 'loan-request/Edit/:id',
        element: <EditLoanReqest />
      },
      {
        path: 'loan-request/View/:id',
        element: <ViewLoanReqest />
      },
      {
        path: 'salary-advance/list',
        element: <SalaryAdvance />
      },
      {
        path: 'salary-advance/Add',
        element: <AddSalaryAdvance />
      },
      {
        path: 'salary-advance/Edit/:id',
        element: <EditSalaryAdvance />
      },
      {
        path: 'salary-advance/View/:id',
        element: <ViewSalaryAdvance />
      }
    ]
  },

  {
    path: 'shift-swap-request',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="list" replace />
      },
      {
        path: 'list',
        element: <LegacyShiftRedirect />
      },
      {
        path: 'add',
        element: <LegacyShiftRedirect />
      },
      {
        path: 'edit/:id',
        element: <LegacyShiftRedirect />
      }
      // {
      //   path: 'view/:id',
      //   element: <ViewTaskTemplate />
      // }
    ]
  },

  {
    path: 'menu-settings',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="list" replace />
      },
      {
        path: 'list',
        element: <MenuSettings />
      },
      {
        path: 'Add',
        element: <MenuSettings />
      },
      {
        path: 'Edit/:id',
        element: <MenuSettings />
      },
      {
        path: 'View/:id',
        element: <MenuSettings />
      }
    ]
  },

  {
    path: 'shift-timing',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="list" replace />
      },
      {
        path: 'list',
        element: <ShiftTimingList />
      },
      {
        path: 'Add',
        element: <AddShiftTiming />
      },
      {
        path: 'Edit/:id',
        element: <ShiftTimingEdit />
      },
      {
        path: 'View/:id',
        element: <ShiftTimingList />
      }
    ]
  },

  // Client Portal - tenant-scoped routes (must come BEFORE non-scoped portal routes)
  {
    path: '/portal/t/:tenantRef/*',
    element: <TenantRouteRedirect />
  },

  // Client Portal - external client-facing routes
  {
    path: '/portal',
    element: <BaseLayout />,
    children: portalRoutes.children
  },

  // Onboarding Portal - tenant-scoped routes
  {
    path: '/onboarding/t/:tenantRef/*',
    element: <TenantRouteRedirect />
  },

  // Onboarding Portal - new employee onboarding
  {
    path: '/onboarding',
    element: <BaseLayout />,
    children: gatePortalChildren(onboardingRoutes.children)
  },

  // Vendor Portal - tenant-scoped routes
  {
    path: '/vendor/t/:tenantRef/*',
    element: <TenantRouteRedirect />
  },

  // Vendor Portal - external recruiting agency portal
  {
    path: '/vendor',
    element: <BaseLayout />,
    children: gatePortalChildren(vendorRoutes.children)
  },

  {
    path: '/components',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'PersonSlide',
        element: <PersonSlide />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  },
  // --- Overtime Module ---
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'overtime/policies',
        element: <OvertimePolicyList />
      },
      {
        path: 'overtime/policies/add',
        element: <OvertimePolicyAdd />
      },
      {
        path: 'overtime/policies/:id/edit',
        element: <OvertimePolicyEdit />
      },
      {
        path: 'overtime/records',
        element: <Overtime />
      },
      {
        path: 'overtime/inbox',
        element: <ManagerOvertimeInbox />
      }
    ]
  },

  // --- Helpdesk Module (Phase 1) ---
  // GREENFIELD — src/content/applications/Helpdesk/ only; does not touch
  // Tickets/ or WorkLog/. See src/content/applications/Helpdesk/index.tsx.
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'helpdesk',
        element: <Helpdesk />,
        children: [
          {
            path: '',
            element: <HelpdeskWorkspace />
          },
          {
            path: 'my-tickets',
            element: <HelpdeskMyTickets />
          },
          {
            path: 'new',
            element: <HelpdeskNewTicket />
          },
          {
            path: 'admin',
            element: <HelpdeskAdmin />
          },
          {
            path: 'tickets/:id',
            element: <HelpdeskTicketDetail />
          }
        ]
      },
      // /support/:formKey — intake form submission page (Phase 2 F/G-FE-3).
      // NOTE: registered inside the authenticated SidebarLayout tree, NOT a
      // public/anonymous layout — the backend intake routes
      // (backend-go/internal/modules/helpdesk/handlers/intake_handler.go)
      // all require JWT + TenantContextMiddleware today, so there is no
      // true anonymous access yet even though allowAnonymous exists on the
      // DTO (deferred to Phase 3 per that file's doc comment). See the
      // G-FE-3 final report for this gap.
      {
        path: 'support/:formKey',
        element: <HelpdeskIntake />
      }
    ]
  },

  // --- Shift Rotation Module ---
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'shift-rotations',
        element: <ShiftRotationList />
      },
      {
        path: 'shift-rotations/add',
        element: <ShiftRotationAdd />
      },
      {
        path: 'shift-rotations/:id/edit',
        element: <ShiftRotationEdit />
      }
    ]
  },

  // --- Shift Allowance Module ---
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'shift-allowance/policies',
        element: <ShiftAllowancePolicyList />
      },
      {
        path: 'shift-allowance/policies/add',
        element: <ShiftAllowancePolicyAdd />
      },
      {
        path: 'shift-allowance/policies/:id/edit',
        element: <ShiftAllowancePolicyEdit />
      },
      {
        path: 'shift-allowance/records',
        element: <ShiftAllowance />
      }
    ]
  },

  // --- Shift Assignment Rule Module ---
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'shift-assignment-rules',
        element: <ShiftAssignmentRuleList />
      },
      {
        path: 'shift-assignment-rules/add',
        element: <ShiftAssignmentRuleAdd />
      },
      {
        path: 'shift-assignment-rules/:id/edit',
        element: <ShiftAssignmentRuleEdit />
      }
    ]
  },

  // --- Specializations Module ---
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      { path: 'specializations', element: <SpecializationList /> },
      { path: 'specializations/add', element: <SpecializationAdd /> },
      { path: 'specializations/:id/edit', element: <SpecializationEdit /> }
    ]
  },

  // --- Practice Areas Module ---
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      { path: 'practice-areas', element: <PracticeAreaList /> },
      { path: 'practice-areas/add', element: <PracticeAreaAdd /> },
      { path: 'practice-areas/:id/edit', element: <PracticeAreaEdit /> }
    ]
  },

  // --- Positions Module ---
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      { path: 'positions', element: <PositionList /> },
      { path: 'positions/add', element: <PositionAdd /> },
      { path: 'positions/:id/edit', element: <PositionEdit /> }
    ]
  },

  // --- Employee Groups (Settings) ---
  // Its own route, outside LeaveShell: groups serve shift plans as well as leave policies.
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      { path: 'employee-groups', element: <EmployeeGroupManager /> }
    ]
  },

  // --- Shifts Hub (Phase 1 — consolidated /shifts parent route) ---
  // Four sub-views served under /shifts/calendar | sheet | plans | coverage.
  // Old paths kept below as Navigate redirects so bookmarks don't break.
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'shifts',
        element: <ShiftsShell />,
        children: [
          { index: true, element: <ShiftsHome /> },
          { path: 'my', element: <MyShiftCalendar /> },
          { path: 'roster', element: <ShiftCalender /> },
          { path: 'roster/assign', element: <AssignPattern /> },
          { path: 'roster/upload', element: <BulkUploadPage /> },
          { path: 'roster/:employeeId/:date', element: <RosterDayPage /> },
          { path: 'patterns', element: <ShiftPlan /> },
          { path: 'patterns/:planId', element: <PatternDetail /> },
          { path: 'on-call', element: <OnCall /> },
          { path: 'on-call/new', element: <OnCallEditor /> },
          { path: 'on-call/:id/edit', element: <OnCallEditor /> },
          { path: 'calendar', element: <LegacyShiftRedirect /> },
          { path: 'sheet', element: <LegacyShiftRedirect /> },
          { path: 'plans', element: <LegacyShiftRedirect /> },
          { path: 'swaps', element: <ShiftSwap /> },
          { path: 'swaps/new', element: <AddShiftSwap /> },
          { path: 'swaps/:id', element: <AddShiftSwap /> },
          { path: 'coverage', element: <ShiftCoverage /> },
          { path: 'unassigned', element: <ShiftUnassigned /> }
        ]
      }
    ]
  },

  // --- Leave Hub (Phase 0.2 — consolidated /leave parent route) ---
  // Legacy paths (leave-list, add-leave-request, view-leave/:id, edit-leave-request/:id)
  // remain intact above and must not be removed.
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'leave',
        element: <LeaveShell />,
        children: [
          { index: true, element: <Navigate to="my" replace /> },
          // /leave/my — v2 home (balance cards + my requests + Apply CTA)
          { path: 'my', element: <MyLeaveHome /> },
          // /leave/my/apply — engine-driven apply form (Agent B)
          { path: 'my/apply', element: <ApplyLeave /> },
          // /leave/my/:id/edit — edit an existing request (Stage 3 FE edit flow)
          { path: 'my/:id/edit', element: <ApplyLeave /> },
          // /leave/my/:id — request detail + cancel actions (Agent B)
          { path: 'my/:id', element: <MyLeaveDetail /> },
          // /leave/team — Phase 1B.5 (manager/HR/admin only — deep-link guard)
          {
            path: 'team',
            element: (
              <LeaveManagerGuard>
                <TeamLeave />
              </LeaveManagerGuard>
            )
          },
          // /leave/approvals — Stage 3 FE slice 3: v2 approver inbox (manager/HR/admin only — deep-link guard)
          {
            path: 'approvals',
            element: (
              <LeaveManagerGuard>
                <ApprovalsInbox />
              </LeaveManagerGuard>
            )
          },
          // /leave/approvals/:id — full-page review for a single pending request (manager-gated)
          {
            path: 'approvals/:id',
            element: (
              <LeaveManagerGuard>
                <LeaveReviewPage />
              </LeaveManagerGuard>
            )
          },
          // /leave/calendar — Phase 1B.7
          { path: 'calendar', element: <LeaveCalendar /> },
          // /leave/employee-groups — moved to /employee-groups (Settings); kept as a redirect for old links
          { path: 'employee-groups', element: <Navigate to="/employee-groups" replace /> },
          // /leave/compoff/earn — employee's comp-off earn requests (list + Apply button)
          { path: 'compoff/earn', element: <CompOffEarnApply /> },
          // /leave/compoff/earn/new — apply form, opened from the list
          { path: 'compoff/earn/new', element: <CompOffEarnNew /> },
          // /leave/compoff/inbox — admin/HR reviews + approves/declines comp-off earn requests
          {
            path: 'compoff/inbox',
            element: (
              <LeaveManagerGuard permissionKey="lv2_compoff_inbox">
                <CompOffEarnInbox />
              </LeaveManagerGuard>
            )
          }
        ]
      }
    ]
  },

  // --- Leave Policy Admin (Wave 1 Sprint B1) ---
  // Routes are outside the /leave shell (admin pages, no view-switcher nav needed).
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      // /leave/policies  — Policy list page
      { path: 'leave/policies', element: <LeavePolicyList /> },
      // /leave/policies/:id — Policy detail / edit page
      { path: 'leave/policies/:id', element: <LeavePolicyDetail /> },
      // /leave/admin/predicates — Attribute Predicate Editor (Wave 2 Sprint P2-A1)
      { path: 'leave/admin/predicates', element: <AttributePredicatesManager /> },
      // WS-H7 — Expense Policy admin. Path matches the WS-B0-seeded sub_category
      // 'expense_policy' route_path (/expense/admin/policies) exactly so the
      // backend-driven sidebar link resolves. Self-gated inside each screen on
      // EXPENSE_ADMIN_POLICY_EDITOR + usePermissions('expense_policy').
      { path: 'expense/admin/policies', element: <ExpensePolicyList /> },
      { path: 'expense/admin/policies/:id', element: <ExpensePolicyDetail /> },
      // No sub_category row was seeded specifically for the category-attribute
      // admin screen; route added consistent with the leave/admin/predicates
      // pattern above (same self-gating, no dedicated sidebar entry).
      { path: 'expense/admin/categories/attributes', element: <ExpenseCategoryAttributeAdmin /> },
      // WS-4 (Expense Multi-Currency Phase 3) — FX rate admin. Same
      // no-dedicated-sidebar-entry / self-gated pattern as the attribute
      // admin route above; reached via the "Manage FX rates" link on
      // PolicyList.tsx.
      { path: 'expense/admin/fx-rates', element: <ExpenseFxRatesAdmin /> },
      // expense/admin/travel-advances route DISABLED as containment for the
      // travel-advance double-charge issue #238 (release-gate 2026-09-03,
      // Blocker B1) — the backend /travel-advance routes it depended on are
      // disabled too, so this screen would 404 on every action.
      // Expense Policy Iteration — expense_approver_map admin CRUD. Same
      // no-dedicated-sidebar-entry / self-gated pattern as the routes above.
      { path: 'expense/admin/approver-map', element: <ExpenseApproverMapAdmin /> },
      // /leave/admin/types — Leave Type Admin (Stage 3 FE)
      { path: 'leave/admin/types', element: <LeaveTypeAdmin /> },
      // /leave/admin/balances — Balance Admin: team view, adjust, opening grant (Stage 3 FE)
      {
        path: 'leave/admin/balances',
        element: (
          <LeaveManagerGuard permissionKey="lv2_balance_admin">
            <LeaveBalanceAdmin />
          </LeaveManagerGuard>
        )
      },
      // /leave/admin/templates — Policy Template Admin: list + instantiate (Phase 0)
      {
        path: 'leave/admin/templates',
        element: (
          <LeaveManagerGuard permissionKey="lv2_leave_policies">
            <LeavePolicyTemplates />
          </LeaveManagerGuard>
        )
      }
    ]
  },

  // --- Onboarding V2 ---
  // {
  //   path: '',
  //   element: <SidebarLayout />,
  //   children: [
  //     {
  //       path: 'nlaunch/invite-codes',
  //       element: (
  //         <LeaveManagerGuard permissionKey="Invite Codes">
  //           <InviteCodesAdmin />
  //         </LeaveManagerGuard>
  //       )
  //     },
  //     {
  //       path: 'nlaunch/plans',
  //       element: <SubscriptionPlansV2 />
  //     },
  //     {
  //       path: 'nlaunch/org-settings',
  //       element: <OrgSettingsV2 />
  //     }
  //   ]
  // },

  // Note: platform-admin routes are intentionally NOT registered here.
  // The ops portal lives in a separate frontend (platform-admin-frontend/)
  // intended for root.cloudripples.io. See platform-admin-frontend/README.md.
];

export default routes;
